/**
 * Dialog Database Renderers
 * Renders dialogs for C# Database export with type-based sealed classes
 */

import { NamingConvention } from '../../../types/exportTemplates';
import type { StringsItem } from '../../stores/stringsStore';
import { applyNamingConvention } from '../core/naming';

/**
 * Render dialogs for C# Database export
 * Creates Database.Dialogs section with nested folder/dialog structure
 * 
 * @param dialogs - Array of dialog objects
 * @param folderPathResolver - Function to resolve folder IDs to paths
 * @param stringsMap - Map of string IDs to StringsItem for translation references
 * @param namingConvention - Naming convention to apply
 * @param indent - Base indentation level (default: 2)
 */
export function renderDialogsDatabaseTemplate(
  dialogs: any[],
  folderPathResolver: (folderId: string | null | undefined) => string,
  stringsMap: Map<string, StringsItem>,
  namingConvention: NamingConvention,
  indent: number = 2
): string {
  if (!dialogs || dialogs.length === 0) {
    return '\t\tpublic static class Dialogs\n\t\t{\n\t\t\t// No dialogs exported\n\t\t}\n\n';
  }

  const tabs = '\t'.repeat(indent);
  let output = `${tabs}public static class Dialogs\n${tabs}{\n`;

  // Build folder structure for dialogs
  const dialogsByFolder: Map<string, any[]> = new Map();
  
  dialogs.forEach(dialog => {
    const folderPath = dialog.folder ? folderPathResolver(dialog.folder) : '';
    const key = folderPath || '_root';
    if (!dialogsByFolder.has(key)) {
      dialogsByFolder.set(key, []);
    }
    dialogsByFolder.get(key)!.push(dialog);
  });

  // Helper to sanitize identifier
  const sanitize = (text: string): string => {
    return text.replace(/[^a-zA-Z0-9_]/g, '_');
  };

  // Helper to get translation key reference
  const getTranslationKeyRef = (stringId: string | null | undefined): string | null => {
    if (!stringId) return null;
    const stringItem = stringsMap.get(stringId);
    if (stringItem) {
      const keyName = stringItem.abbrev || `key_${stringItem.id}`;
      return `TranslationKeys.${sanitize(keyName)}`;
    }
    return null;
  };

  // Render root dialogs (no folder)
  if (dialogsByFolder.has('_root')) {
    const rootDialogs = dialogsByFolder.get('_root')!;
    rootDialogs.forEach(dialog => {
      const dialogIdentifier = sanitize(applyNamingConvention(dialog.dialog_key || dialog.name, namingConvention));
      const dialogTabs = '\t'.repeat(indent + 1);
      
      output += `${dialogTabs}public static class ${dialogIdentifier}\n${dialogTabs}{\n`;
      
      // Add dialog metadata
      output += `${dialogTabs}\tpublic static readonly string Id = "${dialog.id}";\n`;
      output += `${dialogTabs}\tpublic static readonly string Name = "${(dialog.name || '').replace(/"/g, '\\"')}";\n`;
      output += `${dialogTabs}\tpublic static readonly string DialogKey = "${dialog.dialog_key || dialog.name}";\n`;
      
      // Render sections
      if (dialog.data && dialog.data.sections && Array.isArray(dialog.data.sections)) {
        dialog.data.sections.forEach((section: any) => {
          const sectionId = sanitize(applyNamingConvention(section.section_identifier || section.name || 'section', namingConvention));
          output += `\n${dialogTabs}\tpublic static class ${sectionId}\n${dialogTabs}\t{\n`;
          
          // Add section identifier
          output += `${dialogTabs}\t\tpublic static readonly string SectionId = "${section.section_identifier || section.name || ''}";\n`;
          
          // Render fields in this section
          if (section.fields && Array.isArray(section.fields)) {
            section.fields.forEach((field: any, fieldIdx: number) => {
              const fieldName = sanitize(applyNamingConvention(`field_${fieldIdx}_${field.type}`, namingConvention));
              
              // Field text (translatable if string_id exists)
              if (field.text) {
                const translationRef = getTranslationKeyRef(field.string_id);
                if (translationRef) {
                  output += `${dialogTabs}\t\tpublic static readonly TranslationKey ${fieldName}_text = ${translationRef};\n`;
                } else {
                  const escapedText = (field.text || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n');
                  output += `${dialogTabs}\t\tpublic static readonly string ${fieldName}_text = "${escapedText}";\n`;
                }
              }
              
              // Field type
              output += `${dialogTabs}\t\tpublic static readonly string ${fieldName}_type = "${field.type}";\n`;
              
              // Speaker (if present)
              if (field.speaker) {
                output += `${dialogTabs}\t\tpublic static readonly string ${fieldName}_speaker = "${field.speaker}";\n`;
              }
              
              // Navigation (if present)
              if (field.next_section) {
                output += `${dialogTabs}\t\tpublic static readonly string ${fieldName}_next_section = "${field.next_section}";\n`;
              }
              if (field.next_dialog) {
                output += `${dialogTabs}\t\tpublic static readonly string ${fieldName}_next_dialog = "${field.next_dialog}";\n`;
              }
              
              // Options (for question type)
              if (field.options && Array.isArray(field.options) && field.options.length > 0) {
                output += `${dialogTabs}\t\tpublic static class ${fieldName}_options\n${dialogTabs}\t\t{\n`;
                field.options.forEach((option: any, optIdx: number) => {
                  const optionName = sanitize(applyNamingConvention(`option_${optIdx}`, namingConvention));
                  const optionTranslationRef = getTranslationKeyRef(option.string_id);
                  if (optionTranslationRef) {
                    output += `${dialogTabs}\t\t\tpublic static readonly TranslationKey ${optionName} = ${optionTranslationRef};\n`;
                  } else {
                    const escapedOptionText = (option.option_text || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n');
                    output += `${dialogTabs}\t\t\tpublic static readonly string ${optionName} = "${escapedOptionText}";\n`;
                  }
                });
                output += `${dialogTabs}\t\t}\n`;
              }
              
              output += '\n';
            });
          }
          
          output += `${dialogTabs}\t}\n`;
        });
      }
      
      output += `${dialogTabs}}\n\n`;
    });
  }

  // TODO: Render foldered dialogs with nested folder structure
  // For now, skip folders - can be enhanced later

  output += `${tabs}}\n\n`;
  
  console.log(`✅ Rendered ${dialogs.length} dialogs in Database.Dialogs`);
  return output;
}

/**
 * Render DialogDatabase.cs file - Type-based sealed classes
 * Creates a separate database file for dialogs with dictionary-based access pattern
 * Uses type-based sealed classes (DialogTextSection, QuestionSection, etc.) instead of per-section classes
 * 
 * @param dialogs - Array of dialog objects with sections
 * @param folderPathResolver - Function to resolve folder paths
 * @param stringsMap - Map of string IDs to StringsItem for translation references
 * @param namingConvention - Naming convention to apply
 * @param namespace - C# namespace (default: 'Grimoire.Data')
 * @param className - Root class name (default: 'DialogDatabase')
 */
export function renderDialogDatabaseFile(
  dialogs: any[],
  folderPathResolver: (folderId: string | null | undefined) => string,
  stringsMap: Map<string, StringsItem>,
  namingConvention: NamingConvention,
  namespace: string = 'Grimoire.Data',
  className: string = 'DialogDatabase'
): string {
  const hasDialogs = dialogs && dialogs.length > 0;
  
  if (!hasDialogs) {
    console.log('⚠️ No dialogs to export for DialogDatabase - generating empty file');
  } else {
    console.log(`🎭 Rendering DialogDatabase.cs with ${dialogs.length} dialogs (type-based classes)`);
  }

  // Helper to sanitize identifier
  const sanitize = (text: string): string => {
    return text.replace(/[^a-zA-Z0-9_]/g, '_');
  };

  // Helper to get translation key reference
  const getTranslationKeyRef = (stringId: string | null | undefined): string | null => {
    if (!stringId) return null;
    const stringItem = stringsMap.get(stringId);
    if (stringItem) {
      const keyName = stringItem.abbrev || `key_${stringItem.id}`;
      return `TranslationKeys.${sanitize(keyName)}`;
    }
    return null;
  };

  let output = '// Auto-generated Dialog Database - Type-Based Classes\n';
  output += '// This file is AUTO-GENERATED and can be safely regenerated.\n';
  output += '// DO NOT EDIT THIS FILE DIRECTLY - your changes will be lost.\n';
  output += '//\n';
  output += '// Access dialogs: Database.Get<DialogTextSection>("quest_dialog/welcome")\n';
  output += '// Or: var section = DialogDatabase.GetSection<QuestionSection>("quest_dialog/choice")\n';
  output += '// Start dialog: var start = DialogDatabase.GetStartingSection("quest_dialog")\n';
  output += '// Navigate: DialogDatabase.GetNext(currentSection)\n\n';
  output += 'using System;\n';
  output += 'using System.Collections.Generic;\n\n';
  output += `namespace ${namespace}\n{\n`;

  // Generate DialogSection base class
  output += '\t/// <summary>Base class for all dialog sections</summary>\n';
  output += '\tpublic abstract class DialogSection\n\t{\n';
  output += '\t\tpublic string DialogKey { get; set; }\n';
  output += '\t\tpublic string DialogName { get; set; }\n';
  output += '\t\tpublic string SectionId { get; set; }\n';
  output += '\t\tpublic string SectionName { get; set; }\n\n';
  output += '\t\t/// <summary>Get the navigation path to the next section (dialogKey/sectionId)</summary>\n';
  output += '\t\tpublic virtual string GetNextSectionPath() => null;\n';
  output += '\t}\n\n';

  // Collect all field/node types used in dialogs
  const fieldTypes = new Set<string>();
  if (hasDialogs) {
    dialogs.forEach(dialog => {
      // Try new structure (nodes) first
      if (dialog.data?.nodes) {
        dialog.data.nodes.forEach((node: any) => {
          if (node.type) fieldTypes.add(node.type);
        });
      }
      // Fall back to legacy structure (sections/fields)
      else if (dialog.data?.sections) {
        dialog.data.sections.forEach((section: any) => {
          if (section.fields) {
            section.fields.forEach((field: any) => {
              if (field.type) fieldTypes.add(field.type);
            });
          }
        });
      }
    });
  }

  // Generate type-specific sealed classes
  const typeClassMap: Record<string, string> = {
    'dialog': 'DialogTextSection',
    'question': 'QuestionSection',
    'end-text': 'EndTextSection',
    'end-dialog': 'EndDialogSection',
    'context': 'ContextSection',
    'condition': 'ConditionSection'
  };

  // Generate helper classes for conditions and actions
  output += '\t/// <summary>Variable condition for conditional visibility or branching</summary>\n';
  output += '\tpublic class VariableCondition\n\t{\n';
  output += '\t\tpublic string Variable { get; set; }\n';
  output += '\t\tpublic string Operator { get; set; }\n';
  output += '\t\tpublic object Value { get; set; }\n';
  output += '\t}\n\n';

  output += '\t/// <summary>Variable action to execute when an option is selected</summary>\n';
  output += '\tpublic class VariableAction\n\t{\n';
  output += '\t\tpublic string Variable { get; set; }\n';
  output += '\t\tpublic string Operator { get; set; } // "set", "increment", "decrement"\n';
  output += '\t\tpublic object Value { get; set; }\n';
  output += '\t}\n\n';

  // Generate QuestionOptions class (shared by all QuestionSections)
  if (fieldTypes.has('question')) {
    output += '\t/// <summary>Question options with text and navigation</summary>\n';
    output += '\tpublic class QuestionOptions\n\t{\n';
    output += '\t\tpublic List<QuestionOption> Options { get; set; } = new List<QuestionOption>();\n';
    output += '\t}\n\n';

    output += '\t/// <summary>Single question option</summary>\n';
    output += '\tpublic class QuestionOption\n\t{\n';
    output += '\t\tpublic object Text { get; set; } // TranslationKey or string\n';
    output += '\t\tpublic string NextSection { get; set; }\n';
    output += '\t\tpublic string NextDialog { get; set; }\n';
    output += '\t\tpublic string NextDialogSection { get; set; }\n';
    output += '\t\tpublic VariableCondition VisibilityCondition { get; set; }\n';
    output += '\t\tpublic VariableAction VariableAction { get; set; }\n';
    output += '\t}\n\n';
  }

  // Generate DialogTextSection (for type: "dialog")
  if (fieldTypes.has('dialog')) {
    output += '\t/// <summary>Dialog text section - standard dialog with text and optional speaker</summary>\n';
    output += '\tpublic sealed class DialogTextSection : DialogSection\n\t{\n';
    output += '\t\tpublic object Text { get; set; } // TranslationKey or string\n';
    output += '\t\tpublic string Speaker { get; set; }\n';
    output += '\t\tpublic string NextSection { get; set; }\n';
    output += '\t\tpublic string NextDialog { get; set; }\n';
    output += '\t\tpublic string NextDialogSection { get; set; }\n\n';
    output += '\t\tpublic override string GetNextSectionPath()\n\t\t{\n';
    output += '\t\t\treturn string.IsNullOrEmpty(NextSection) ? null : $"{DialogKey}/{NextSection}";\n';
    output += '\t\t}\n';
    output += '\t}\n\n';
  }

  // Generate QuestionSection (for type: "question")
  if (fieldTypes.has('question')) {
    output += '\t/// <summary>Question section - presents choices to the player</summary>\n';
    output += '\tpublic sealed class QuestionSection : DialogSection\n\t{\n';
    output += '\t\tpublic object Text { get; set; } // TranslationKey or string\n';
    output += '\t\tpublic string Speaker { get; set; }\n';
    output += '\t\tpublic QuestionOptions Options { get; set; }\n\n';
    output += '\t\t/// <summary>Get navigation path for a specific option index</summary>\n';
    output += '\t\tpublic string GetNextSectionForOption(int optionIndex)\n\t\t{\n';
    output += '\t\t\tif (Options?.Options == null || optionIndex < 0 || optionIndex >= Options.Options.Count)\n';
    output += '\t\t\t\treturn null;\n';
    output += '\t\t\tvar option = Options.Options[optionIndex];\n';
    output += '\t\t\tif (!string.IsNullOrEmpty(option.NextDialog))\n';
    output += '\t\t\t\treturn $"{option.NextDialog}/{option.NextDialogSection ?? ""}";\n';
    output += '\t\t\treturn string.IsNullOrEmpty(option.NextSection) ? null : $"{DialogKey}/{option.NextSection}";\n';
    output += '\t\t}\n';
    output += '\t}\n\n';
  }

  // Generate EndTextSection (for type: "end-text")
  if (fieldTypes.has('end-text')) {
    output += '\t/// <summary>End text section - concludes dialog with text but allows continuation</summary>\n';
    output += '\tpublic sealed class EndTextSection : DialogSection\n\t{\n';
    output += '\t\tpublic object Text { get; set; } // TranslationKey or string\n';
    output += '\t\tpublic string Speaker { get; set; }\n';
    output += '\t\tpublic string NextSection { get; set; }\n';
    output += '\t\tpublic string NextDialog { get; set; }\n';
    output += '\t\tpublic string NextDialogSection { get; set; }\n\n';
    output += '\t\tpublic override string GetNextSectionPath()\n\t\t{\n';
    output += '\t\t\treturn string.IsNullOrEmpty(NextSection) ? null : $"{DialogKey}/{NextSection}";\n';
    output += '\t\t}\n';
    output += '\t}\n\n';
  }

  // Generate EndDialogSection (for type: "end-dialog")
  if (fieldTypes.has('end-dialog')) {
    output += '\t/// <summary>End dialog section - terminates the dialog completely</summary>\n';
    output += '\tpublic sealed class EndDialogSection : DialogSection\n\t{\n';
    output += '\t\tpublic object Text { get; set; } // TranslationKey or string\n';
    output += '\t\tpublic string Speaker { get; set; }\n';
    output += '\t}\n\n';
  }

  // Generate ContextSection (for type: "context")
  if (fieldTypes.has('context')) {
    output += '\t/// <summary>Context section - provides context without interaction</summary>\n';
    output += '\tpublic sealed class ContextSection : DialogSection\n\t{\n';
    output += '\t\tpublic object Text { get; set; } // TranslationKey or string\n';
    output += '\t\tpublic string NextSection { get; set; }\n';
    output += '\t\tpublic string NextDialog { get; set; }\n';
    output += '\t\tpublic string NextDialogSection { get; set; }\n\n';
    output += '\t\tpublic override string GetNextSectionPath()\n\t\t{\n';
    output += '\t\t\treturn string.IsNullOrEmpty(NextSection) ? null : $"{DialogKey}/{NextSection}";\n';
    output += '\t\t}\n';
    output += '\t}\n\n';
  }

  // Generate ConditionSection (for type: "condition")
  if (fieldTypes.has('condition')) {
    output += '\t/// <summary>Single condition option with condition check and navigation target</summary>\n';
    output += '\tpublic class ConditionOptionData\n\t{\n';
    output += '\t\tpublic string Variable { get; set; }\n';
    output += '\t\tpublic string Operator { get; set; }\n';
    output += '\t\tpublic object Value { get; set; }\n';
    output += '\t\tpublic string NextSection { get; set; }\n';
    output += '\t\tpublic string NextDialog { get; set; }\n';
    output += '\t\tpublic string NextDialogSection { get; set; }\n';
    output += '\t}\n\n';

    output += '\t/// <summary>Condition section - branches based on variable conditions</summary>\n';
    output += '\tpublic sealed class ConditionSection : DialogSection\n\t{\n';
    output += '\t\t// Legacy single condition support (for backwards compatibility)\n';
    output += '\t\tpublic string ConditionVariable { get; set; }\n';
    output += '\t\tpublic string ConditionCheckVariable { get; set; } // For range types: which variable to check if it falls within the range\n';
    output += '\t\tpublic string ConditionOperator { get; set; }\n';
    output += '\t\tpublic object ConditionValue { get; set; }\n';
    output += '\t\tpublic string NextSectionTrue { get; set; }\n';
    output += '\t\tpublic string NextSectionFalse { get; set; }\n';
    output += '\t\tpublic string NextDialogTrue { get; set; }\n';
    output += '\t\tpublic string NextDialogFalse { get; set; }\n';
    output += '\t\tpublic string NextDialogSectionTrue { get; set; }\n';
    output += '\t\tpublic string NextDialogSectionFalse { get; set; }\n\n';
    output += '\t\t// Multi-condition support (for complex branching)\n';
    output += '\t\tpublic List<ConditionOptionData> ConditionOptions { get; set; }\n\n';
    output += '\t\t/// <summary>Get next path based on condition result (legacy single condition)</summary>\n';
    output += '\t\tpublic string GetNextSectionPath(bool conditionResult)\n\t\t{\n';
    output += '\t\t\tif (conditionResult)\n';
    output += '\t\t\t{\n';
    output += '\t\t\t\tif (!string.IsNullOrEmpty(NextDialogTrue))\n';
    output += '\t\t\t\t\treturn $"{NextDialogTrue}/{NextDialogSectionTrue ?? ""}";\n';
    output += '\t\t\t\treturn string.IsNullOrEmpty(NextSectionTrue) ? null : $"{DialogKey}/{NextSectionTrue}";\n';
    output += '\t\t\t}\n';
    output += '\t\t\telse\n';
    output += '\t\t\t{\n';
    output += '\t\t\t\tif (!string.IsNullOrEmpty(NextDialogFalse))\n';
    output += '\t\t\t\t\treturn $"{NextDialogFalse}/{NextDialogSectionFalse ?? ""}";\n';
    output += '\t\t\t\treturn string.IsNullOrEmpty(NextSectionFalse) ? null : $"{DialogKey}/{NextSectionFalse}";\n';
    output += '\t\t\t}\n';
    output += '\t\t}\n\n';
    output += '\t\t/// <summary>Get navigation path for a specific condition option index</summary>\n';
    output += '\t\tpublic string GetNextSectionForCondition(int conditionIndex)\n\t\t{\n';
    output += '\t\t\tif (ConditionOptions == null || conditionIndex < 0 || conditionIndex >= ConditionOptions.Count)\n';
    output += '\t\t\t\treturn null;\n';
    output += '\t\t\tvar condOption = ConditionOptions[conditionIndex];\n';
    output += '\t\t\tif (!string.IsNullOrEmpty(condOption.NextDialog))\n';
    output += '\t\t\t\treturn $"{condOption.NextDialog}/{condOption.NextDialogSection ?? ""}";\n';
    output += '\t\t\treturn string.IsNullOrEmpty(condOption.NextSection) ? null : $"{DialogKey}/{condOption.NextSection}";\n';
    output += '\t\t}\n';
    output += '\t}\n\n';
  }

  // Generate dictionary entries
  const dictionaryEntries: Array<{ key: string; className: string; instance: string }> = [];

  if (hasDialogs) {
    dialogs.forEach(dialog => {
    const dialogKey = dialog.dialog_key || dialog.name;
    const dialogName = dialog.name || dialogKey;

    // Try new structure (nodes) first
    if (dialog.data?.nodes && Array.isArray(dialog.data.nodes)) {
      dialog.data.nodes.forEach((node: any) => {
        const nodeId = node.node_identifier || node.name || 'node';
        const nodeName = node.name || nodeId;
        const dictionaryKey = `${dialogKey}/${nodeId}`;

        const nodeType = node.type;
        const sectionClassName = typeClassMap[nodeType] || 'DialogTextSection';

        // Build dictionary entry instance based on node type
        let instanceCode = `new ${sectionClassName}\n\t\t\t{\n`;
        instanceCode += `\t\t\t\tDialogKey = "${dialogKey}",\n`;
        instanceCode += `\t\t\t\tDialogName = "${dialogName.replace(/"/g, '\\"')}",\n`;
        instanceCode += `\t\t\t\tSectionId = "${nodeId}",\n`;
        instanceCode += `\t\t\t\tSectionName = "${nodeName.replace(/"/g, '\\"')}",\n`;

        // Process node (same logic as primaryField below, but directly on node)
        const primaryField = node; // Node IS the field in the new structure
        const fieldType = nodeType;

        // Add type-specific properties
        if (fieldType === 'dialog' || fieldType === 'end-text' || fieldType === 'end-dialog' || fieldType === 'context') {
        // These types have Text, Speaker (optional), and Navigation
        if (primaryField.text) {
          const translationRef = getTranslationKeyRef(primaryField.string_id);
          if (translationRef) {
            instanceCode += `\t\t\t\tText = ${translationRef},\n`;
          } else {
            const escapedText = (primaryField.text || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n');
            instanceCode += `\t\t\t\tText = "${escapedText}",\n`;
          }
        }

        if (primaryField.speaker !== undefined && primaryField.speaker !== null) {
          instanceCode += `\t\t\t\tSpeaker = "${primaryField.speaker}",\n`;
        }

        // Navigation (not for end-dialog)
        if (fieldType !== 'end-dialog') {
          const nextNode = primaryField.next_node || primaryField.next_section;
          if (nextNode) {
            instanceCode += `\t\t\t\tNextSection = "${nextNode}",\n`;
          }
          if (primaryField.next_dialog) {
            instanceCode += `\t\t\t\tNextDialog = "${primaryField.next_dialog}",\n`;
            const nextDialogNode = primaryField.next_dialog_node || primaryField.next_dialog_section;
            if (nextDialogNode) {
              instanceCode += `\t\t\t\tNextDialogSection = "${nextDialogNode}",\n`;
            }
          }
        }
      } else if (fieldType === 'question') {
        // Question type has Text, Speaker, and Options
        if (primaryField.text) {
          const translationRef = getTranslationKeyRef(primaryField.string_id);
          if (translationRef) {
            instanceCode += `\t\t\t\tText = ${translationRef},\n`;
          } else {
            const escapedText = (primaryField.text || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n');
            instanceCode += `\t\t\t\tText = "${escapedText}",\n`;
          }
        }

        if (primaryField.speaker !== undefined && primaryField.speaker !== null) {
          instanceCode += `\t\t\t\tSpeaker = "${primaryField.speaker}",\n`;
        }

        // Build options
        if (primaryField.options && Array.isArray(primaryField.options) && primaryField.options.length > 0) {
          instanceCode += `\t\t\t\tOptions = new QuestionOptions\n\t\t\t\t{\n`;
          instanceCode += `\t\t\t\t\tOptions = new List<QuestionOption>\n\t\t\t\t\t{\n`;
          
          primaryField.options.forEach((option: any) => {
            instanceCode += `\t\t\t\t\t\tnew QuestionOption\n\t\t\t\t\t\t{\n`;
            
            // Option text
            const optionTranslationRef = getTranslationKeyRef(option.string_id);
            if (optionTranslationRef) {
              instanceCode += `\t\t\t\t\t\t\tText = ${optionTranslationRef},\n`;
            } else {
              const escapedOptionText = (option.option_text || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n');
              instanceCode += `\t\t\t\t\t\t\tText = "${escapedOptionText}",\n`;
            }

            // Option navigation
            const optNextNode = option.next_node || option.next_section;
            if (optNextNode) {
              instanceCode += `\t\t\t\t\t\t\tNextSection = "${optNextNode}",\n`;
            }
            if (option.next_dialog) {
              instanceCode += `\t\t\t\t\t\t\tNextDialog = "${option.next_dialog}",\n`;
              const optNextDialogNode = option.next_dialog_node || option.next_dialog_section;
              if (optNextDialogNode) {
                instanceCode += `\t\t\t\t\t\t\tNextDialogSection = "${optNextDialogNode}",\n`;
              }
            }

            // Visibility condition
            if (option.visibilityCondition) {
              instanceCode += `\t\t\t\t\t\t\tVisibilityCondition = new VariableCondition\n\t\t\t\t\t\t\t{\n`;
              instanceCode += `\t\t\t\t\t\t\t\tVariable = "${option.visibilityCondition.variable || ''}",\n`;
              instanceCode += `\t\t\t\t\t\t\t\tOperator = "${option.visibilityCondition.operator || '=='}",\n`;
              const visCondValue = option.visibilityCondition.value;
              if (typeof visCondValue === 'string') {
                instanceCode += `\t\t\t\t\t\t\t\tValue = "${visCondValue}",\n`;
              } else {
                instanceCode += `\t\t\t\t\t\t\t\tValue = ${visCondValue},\n`;
              }
              instanceCode += `\t\t\t\t\t\t\t},\n`;
            }

            // Variable action
            if (option.variableAction) {
              instanceCode += `\t\t\t\t\t\t\tVariableAction = new VariableAction\n\t\t\t\t\t\t\t{\n`;
              instanceCode += `\t\t\t\t\t\t\t\tVariable = "${option.variableAction.variable || ''}",\n`;
              instanceCode += `\t\t\t\t\t\t\t\tOperator = "${option.variableAction.operator || 'set'}",\n`;
              const varActValue = option.variableAction.value;
              if (typeof varActValue === 'string') {
                instanceCode += `\t\t\t\t\t\t\t\tValue = "${varActValue}",\n`;
              } else {
                instanceCode += `\t\t\t\t\t\t\t\tValue = ${varActValue},\n`;
              }
              instanceCode += `\t\t\t\t\t\t\t},\n`;
            }

            instanceCode += `\t\t\t\t\t\t},\n`;
          });

          instanceCode += `\t\t\t\t\t}\n`;
          instanceCode += `\t\t\t\t},\n`;
        }
      } else if (fieldType === 'condition') {
        // Condition type has condition data and conditional navigation
        if (primaryField.condition) {
          instanceCode += `\t\t\t\tConditionVariable = "${primaryField.condition.variable || ''}",\n`;
          if (primaryField.condition.checkVariable) {
            instanceCode += `\t\t\t\tConditionCheckVariable = "${primaryField.condition.checkVariable}",\n`;
          }
          instanceCode += `\t\t\t\tConditionOperator = "${primaryField.condition.operator || '=='}",\n`;
          const condValue = primaryField.condition.value;
          if (typeof condValue === 'string') {
            instanceCode += `\t\t\t\tConditionValue = "${condValue}",\n`;
          } else {
            instanceCode += `\t\t\t\tConditionValue = ${condValue},\n`;
          }
        }

        // Legacy conditional navigation (backwards compatibility)
        if (primaryField.next_section_true) {
          instanceCode += `\t\t\t\tNextSectionTrue = "${primaryField.next_section_true}",\n`;
        }
        if (primaryField.next_section_false) {
          instanceCode += `\t\t\t\tNextSectionFalse = "${primaryField.next_section_false}",\n`;
        }
        if (primaryField.next_dialog_true) {
          instanceCode += `\t\t\t\tNextDialogTrue = "${primaryField.next_dialog_true}",\n`;
          if (primaryField.next_dialog_section_true) {
            instanceCode += `\t\t\t\tNextDialogSectionTrue = "${primaryField.next_dialog_section_true}",\n`;
          }
        }
        if (primaryField.next_dialog_false) {
          instanceCode += `\t\t\t\tNextDialogFalse = "${primaryField.next_dialog_false}",\n`;
          if (primaryField.next_dialog_section_false) {
            instanceCode += `\t\t\t\tNextDialogSectionFalse = "${primaryField.next_dialog_section_false}",\n`;
          }
        }

        // Multi-condition options (for complex branching)
        if (primaryField.conditionOptions && Array.isArray(primaryField.conditionOptions) && primaryField.conditionOptions.length > 0) {
          instanceCode += `\t\t\t\tConditionOptions = new List<ConditionOptionData>\n\t\t\t\t{\n`;
          
          primaryField.conditionOptions.forEach((condOption: any) => {
            instanceCode += `\t\t\t\t\tnew ConditionOptionData\n\t\t\t\t\t{\n`;
            
            // Condition check
            if (condOption.variable) {
              instanceCode += `\t\t\t\t\t\tVariable = "${condOption.variable}",\n`;
            }
            if (condOption.operator) {
              instanceCode += `\t\t\t\t\t\tOperator = "${condOption.operator}",\n`;
            }
            if (condOption.value !== undefined && condOption.value !== null) {
              const optCondValue = condOption.value;
              if (typeof optCondValue === 'string') {
                instanceCode += `\t\t\t\t\t\tValue = "${optCondValue}",\n`;
              } else {
                instanceCode += `\t\t\t\t\t\tValue = ${optCondValue},\n`;
              }
            }
            
            // Navigation
            const condNextNode = condOption.next_node || condOption.next_section;
            if (condNextNode) {
              instanceCode += `\t\t\t\t\t\tNextSection = "${condNextNode}",\n`;
            }
            if (condOption.next_dialog) {
              instanceCode += `\t\t\t\t\t\tNextDialog = "${condOption.next_dialog}",\n`;
              const condNextDialogNode = condOption.next_dialog_node || condOption.next_dialog_section;
              if (condNextDialogNode) {
                instanceCode += `\t\t\t\t\t\tNextDialogSection = "${condNextDialogNode}",\n`;
              }
            }
            
            instanceCode += `\t\t\t\t\t},\n`;
          });
          
          instanceCode += `\t\t\t\t},\n`;
        }
      }

      instanceCode += '\t\t\t}';

      dictionaryEntries.push({ key: dictionaryKey, className: sectionClassName, instance: instanceCode });
      });
    }
    });
  }

  // Generate DialogDatabase static class with Dialogs dictionary
  output += `\t/// <summary>Static database for dialog sections with dictionary-based access</summary>\n`;
  output += `\tpublic static class ${className}\n\t{\n`;

  // Generate Dialogs dictionary
  output += '\t\t/// <summary>Dictionary of all dialog sections, keyed by "dialogKey/sectionId"</summary>\n';
  output += '\t\tpublic static readonly IReadOnlyDictionary<string, DialogSection> Dialogs =\n';
  output += '\t\t\tnew Dictionary<string, DialogSection>\n\t\t\t{\n';

  if (dictionaryEntries.length === 0) {
    output += '\t\t\t\t// No dialogs exported\n';
  } else {
    dictionaryEntries.forEach(({ key, instance }) => {
      output += `\t\t\t\t["${key}"] = ${instance},\n`;
    });
  }

  output += '\t\t\t};\n\n';

  // Generate typed GetSection<T> method
  output += '\t\t/// <summary>\n';
  output += '\t\t/// Get a dialog section by path with type safety.\n';
  output += '\t\t/// </summary>\n';
  output += '\t\t/// <typeparam name="T">The expected section type</typeparam>\n';
  output += '\t\t/// <param name="path">The section path (dialogKey/sectionId)</param>\n';
  output += '\t\t/// <returns>The typed section, or null if not found or type mismatch</returns>\n';
  output += '\t\tpublic static T GetSection<T>(string path) where T : DialogSection\n\t\t{\n';
  output += '\t\t\tif (string.IsNullOrEmpty(path)) return null;\n';
  output += '\t\t\treturn Dialogs.TryGetValue(path, out var section) ? section as T : null;\n';
  output += '\t\t}\n\n';

  // Generate GetNext navigation method
  output += '\t\t/// <summary>\n';
  output += '\t\t/// Get the next dialog section based on navigation fields.\n';
  output += '\t\t/// Returns null if the section is an endpoint or no navigation is defined.\n';
  output += '\t\t/// For question fields with multiple options, this returns null - you must handle option navigation manually.\n';
  output += '\t\t/// </summary>\n';
  output += '\t\t/// <param name="current">The current dialog section</param>\n';
  output += '\t\t/// <returns>The next DialogSection, or null if at an endpoint</returns>\n';
  output += '\t\tpublic static DialogSection GetNext(DialogSection current)\n\t\t{\n';
  output += '\t\t\tif (current == null) return null;\n\n';
  output += '\t\t\t// Use the virtual method to get the next section path\n';
  output += '\t\t\tstring nextPath = current.GetNextSectionPath();\n';
  output += '\t\t\tif (string.IsNullOrEmpty(nextPath)) return null;\n\n';
  output += '\t\t\t// Look up the next section in the dictionary\n';
  output += '\t\t\treturn Dialogs.TryGetValue(nextPath, out var nextSection) ? nextSection : null;\n';
  output += '\t\t}\n\n';

  // Generate DialogInfo class and DialogMetadata dictionary
  output += '\t\t/// <summary>Dialog metadata including starting sections</summary>\n';
  output += '\t\tpublic class DialogInfo\n\t\t{\n';
  output += '\t\t\tpublic string DialogKey { get; set; }\n';
  output += '\t\t\tpublic string DialogName { get; set; }\n';
  output += '\t\t\tpublic string StartingSection { get; set; }\n';
  output += '\t\t}\n\n';

  output += '\t\t/// <summary>Dictionary of dialog metadata, keyed by dialog key</summary>\n';
  output += '\t\tpublic static readonly IReadOnlyDictionary<string, DialogInfo> DialogMetadata =\n';
  output += '\t\t\tnew Dictionary<string, DialogInfo>\n\t\t\t{\n';

  // Build dialog metadata entries
  const dialogMetadataEntries: Array<{ dialogKey: string; dialogName: string; startingSection: string }> = [];

  if (hasDialogs) {
    dialogs.forEach(dialog => {
      const dialogKey = dialog.dialog_key || dialog.name;
      const dialogName = dialog.name || dialogKey;
      const startingSection = dialog.data?.startingSection || 
        (dialog.data?.sections && dialog.data.sections.length > 0 
          ? dialog.data.sections[0].section_identifier 
          : '');
      
      dialogMetadataEntries.push({ dialogKey, dialogName, startingSection });
    });
  }

  if (dialogMetadataEntries.length === 0) {
    output += '\t\t\t\t// No dialog metadata\n';
  } else {
    dialogMetadataEntries.forEach(({ dialogKey, dialogName, startingSection }) => {
      output += `\t\t\t\t["${dialogKey}"] = new DialogInfo\n\t\t\t\t{\n`;
      output += `\t\t\t\t\tDialogKey = "${dialogKey}",\n`;
      output += `\t\t\t\t\tDialogName = "${dialogName.replace(/"/g, '\\"')}",\n`;
      output += `\t\t\t\t\tStartingSection = "${startingSection}",\n`;
      output += `\t\t\t\t},\n`;
    });
  }

  output += '\t\t\t};\n\n';

  // Add helper method to get starting section
  output += '\t\t/// <summary>\n';
  output += '\t\t/// Get the starting section for a dialog by dialog key.\n';
  output += '\t\t/// </summary>\n';
  output += '\t\t/// <param name="dialogKey">The dialog key</param>\n';
  output += '\t\t/// <returns>The starting section, or null if dialog not found</returns>\n';
  output += '\t\tpublic static DialogSection GetStartingSection(string dialogKey)\n\t\t{\n';
  output += '\t\t\tif (string.IsNullOrEmpty(dialogKey)) return null;\n';
  output += '\t\t\tif (!DialogMetadata.TryGetValue(dialogKey, out var info)) return null;\n';
  output += '\t\t\tif (string.IsNullOrEmpty(info.StartingSection)) return null;\n';
  output += '\t\t\tstring path = $"{dialogKey}/{info.StartingSection}";\n';
  output += '\t\t\treturn GetSection<DialogSection>(path);\n';
  output += '\t\t}\n\n';

  // Add typed helper methods for common types
  output += '\t\t/// <summary>Get starting section with type safety</summary>\n';
  output += '\t\tpublic static T GetStartingSection<T>(string dialogKey) where T : DialogSection\n\t\t{\n';
  output += '\t\t\treturn GetStartingSection(dialogKey) as T;\n';
  output += '\t\t}\n';

  output += '\t}\n';
  output += '}\n';

  if (hasDialogs) {
    console.log(`✅ Rendered DialogDatabase.cs with ${dictionaryEntries.length} section entries`);
  } else {
    console.log(`✅ Rendered empty DialogDatabase.cs`);
  }
  return output;
}
